/**
 * RESPONSIVE BREAKPOINT CONVENTION — follow this in all batches:
 *
 *  lg+ (≥1024px)  Desktop: persistent sidebar on left, topbar with full user info + action buttons.
 *  < lg (≤1023px) Tablet / mobile: sidebar hidden; a hamburger button in the topbar opens it as
 *                 a Headless UI Dialog off-canvas drawer that slides in from the left.
 *  < md (≤767px)  Mobile: topbar user info (name, email, role badge) and action buttons (locale
 *                 switcher, change password, sign out) collapse into a Headless UI Menu triggered
 *                 by an avatar icon button. Only hamburger + brand title + avatar menu are visible.
 *
 *  Main content padding: p-3 sm:p-4 lg:p-6
 */

import { useState, useEffect } from 'react';
import { Outlet, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogPanel,
  DialogBackdrop,
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { Bars3Icon, XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { Sidebar } from './Sidebar';
import { LocaleSwitcher } from '@/components/LocaleSwitcher';
import { NotificationsDropdown } from '@/components/NotificationsDropdown';
import { ChangeMyPasswordDialog } from '@/components/ChangeMyPasswordDialog';
import { useAuthStore } from '@/stores/authStore';
import { useBranding } from '@/hooks/useBranding';

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const logout = useAuthStore((s) => s.logout);
  const { companyName } = useBranding();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const { t } = useTranslation(['common', 'admin']);

  // Close drawer on route change (covers back-button navigation too)
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Platform-scoped roles have their own shell — redirect them out of the tenant shell.
  if (user?.role === 'platform_admin' || user?.role === 'super_admin') {
    return <Navigate to="/platform" replace />;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleChangePassword = () => {
    setChangePasswordOpen(true);
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* ── Desktop sidebar (lg+) ─────────────────────────────── */}
      <div className="hidden lg:flex">
        <Sidebar collapsible />
      </div>

      {/* ── Mobile/tablet off-canvas drawer (< lg) ───────────── */}
      <Transition show={drawerOpen}>
        <Dialog onClose={() => setDrawerOpen(false)} className="relative z-50 lg:hidden">
          {/* Backdrop */}
          <TransitionChild
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="fixed inset-0 bg-black/50" />
          </TransitionChild>

          {/* Slide-in panel */}
          <div className="fixed inset-0 flex">
            <TransitionChild
              enter="transition ease-in-out duration-200 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-200 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <DialogPanel className="relative flex w-64 flex-col">
                {/* Close button inside drawer */}
                <div className="absolute top-0 right-0 -mr-10 pt-3">
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(false)}
                    className="ml-1 flex h-8 w-8 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-white"
                    aria-label={t('common:actions.closeMenu')}
                  >
                    <XMarkIcon className="h-5 w-5 text-white" aria-hidden="true" />
                  </button>
                </div>
                <Sidebar onNavClick={() => setDrawerOpen(false)} />
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* ── Main content column ───────────────────────────────── */}
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        {/* Topbar */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-3 sm:px-4 lg:px-6">

          {/* Left: hamburger + brand title (< lg only) */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('common:actions.openMenu')}
            >
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <span className="lg:hidden text-sm font-semibold text-gray-800 truncate">
              {companyName}
            </span>
          </div>

          {/* Right: locale switcher + user dropdown */}
          <div className="flex items-center gap-2 shrink-0">
            <LocaleSwitcher variant="header-light" />

            {isAdmin && <NotificationsDropdown />}

            <Menu>
              {/* Name + email as the menu trigger — always visible who is
                  logged in; tighter width cap on small screens. */}
              <MenuButton
                className="inline-flex items-center rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                aria-label={t('common:actions.openUserMenu')}
              >
                {user ? (
                  <span className="flex flex-col items-start min-w-0 max-w-[9rem] md:max-w-[14rem] text-left leading-tight">
                    <span className="w-full truncate text-sm font-medium text-gray-800">
                      {user.name}
                    </span>
                    <span className="w-full truncate text-xs text-gray-500">{user.email}</span>
                  </span>
                ) : (
                  <UserCircleIcon className="h-7 w-7 shrink-0" aria-hidden="true" />
                )}
              </MenuButton>

              <MenuItems
                anchor="bottom end"
                className="z-50 mt-1 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none divide-y divide-gray-100"
              >
                {/* Who is logged in — the only place it's visible on mobile */}
                {user && (
                  <div className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                )}
                <div className="py-1">
                  <MenuItem>
                    <button
                      onClick={handleChangePassword}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                    >
                      {t('common:actions.changePassword')}
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                    >
                      {t('common:actions.signOut')}
                    </button>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-3 sm:p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      <ChangeMyPasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </div>
  );
}
